/* hs-eslint ignored failing-rules */
/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/rules-of-hooks */

'use es6';

import { refreshStoreData } from 'conversations-thread-data/store/public/actions';
import { useEffect, useMemo, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { useChannelConfigurations } from 'conversations-thread-data/channel-configuration/public/hooks';
import { setTracker, setBeaconTracker } from '../../usage-tracking/clients/usageTracker';
import { useThreadDetails } from '../../thread-details-feature/public/hooks/useThreadDetails';
import { getOriginalGenericChannelId } from 'conversations-thread-data/thread-details/public/operators';
import { visitorUpdated } from 'conversations-thread-data/visitor/actions/visitorUpdated';
import { threadIdChanged } from 'conversations-thread-data/thread-details/public/actions';
import { provideThreadView } from '../decorators/provideThreadView';
import { ticketDisassociated } from 'conversations-thread-data/associations/public/actions';
import { useThreadViewRealtime } from '../../thread-view-realtime/public/hooks/useThreadViewRealtime';
import { getAvailableEditorConfigs } from '../../tabbed-editors/operators/getAvailableEditorConfigs';
import { useThreadDetailsWatcher } from '../hooks/useThreadDetailsWatcher';
import { getUsageTrackingChannel } from 'conversations-thread-data/usage-tracking/public/trackerChannelIdMapping';
import Visitor from 'conversations-internal-schema/visitor/records/Visitor';
const noop = () => {};

/**
 * @description This component manages data
 * syncing between the props passed into the thread
 * view and the inner thread-view store. This stuff
 * can't happen in the ThreadViewWrapper, since that isn't
 * connected to the inner store (and shouldn't be, as it's
 * children won't always necessarily be connected to that
 * store either).
 */
export const ThreadViewManager = /*#__PURE__*/forwardRef(({
  beaconTracker,
  inboxId,
  threadChannels,
  threadId,
  ticketCount,
  tracker,
  lifecycle,
  vid,
  visitor
}, ref) => {
  const {
    onThreadTrackingContextChange = noop,
    onThreadDetailsChange = noop
  } = lifecycle;
  const dispatch = useDispatch();
  const {
    asyncThreadDetails
  } = useThreadDetails({
    threadId
  });
  const threadDetails = getData(asyncThreadDetails);
  const originalGenericChannelId = getOriginalGenericChannelId(threadDetails);
  const {
    asyncChannelConfigurations
  } = useChannelConfigurations();
  const configurations = getData(asyncChannelConfigurations);
  const filteredConfigurations = getAvailableEditorConfigs({
    configurations,
    originalGenericChannelId,
    includeNonGenericChannels: false
  });
  const availableChannels = filteredConfigurations && filteredConfigurations.keySeq().toArray();

  // Wire up Lifecycle Hooks
  useThreadDetailsWatcher({
    threadDetails,
    onChange: onThreadDetailsChange
  });
  useEffect(() => {
    onThreadTrackingContextChange({
      channel: getUsageTrackingChannel(originalGenericChannelId)
    });
  }, [originalGenericChannelId, onThreadTrackingContextChange]);

  // Core Realtime Setup
  useThreadViewRealtime({
    asyncThreadDetails,
    inboxId,
    threadId,
    threadChannels,
    vid,
    enableThreadPresence: true
  });

  // Imperative API used by the Inbox to hard-refresh this app
  if (ref) {
    ref.current = useMemo(() => ({
      hardRefresh: ({
        threadDetails: nextThreadDetails,
        threadHistory: nextThreadHistory,
        omnibusThreadId
      }) => {
        dispatch(refreshStoreData({
          threadDetails: nextThreadDetails,
          threadHistory: nextThreadHistory,
          threadId: omnibusThreadId
        }));
      },
      ticketDisassociated: ({
        threadId: passedThreadId,
        ticketId
      }) => {
        dispatch(ticketDisassociated({
          threadId: passedThreadId,
          ticketId
        }));
      }
    }), [dispatch]);
  }

  // Tracking Setup and Internal Application events
  useEffect(() => {
    setTracker(tracker, {
      availableChannels,
      threadId,
      ticketCount,
      originalGenericChannelId
    });
  }, [availableChannels, tracker, threadId, originalGenericChannelId, ticketCount]);
  useEffect(() => {
    setBeaconTracker(beaconTracker);
  }, [beaconTracker]);
  useEffect(() => {
    dispatch(threadIdChanged({
      threadId: Number(threadId)
    }));
  }, [dispatch, threadId]);
  useEffect(() => {
    dispatch(visitorUpdated({
      threadId,
      visitor: visitor || Visitor()
    }));
  }, [dispatch, threadId, visitor]);
  return null;
});
ThreadViewManager.displayName = 'ThreadViewManager';
ThreadViewManager.propTypes = {
  threadId: PropTypes.string.isRequired,
  ticketCount: PropTypes.number,
  tracker: PropTypes.object.isRequired
};
ThreadViewManager.defaultProps = {
  lifecycle: {}
};
export default provideThreadView(ThreadViewManager);